import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FiSave, FiUsers } from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { ArtistsContent } from '../../../types/content'
import { toast } from 'react-hot-toast'

const ArtistsEditor: React.FC = () => {
  const { content, loading, saving, saveContent } = useContent('artists')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<ArtistsContent>({
    defaultValues: {
      ctaTitle: 'Ready to Book Live Music?',
      ctaDescription: 'Contact us to discuss your event needs and we\'ll help you find the perfect musicians to make your occasion truly special.',
      ctaButtonText: 'Get in Touch'
    }
  })

  const watchedData = watch()

  // Load content when available
  useEffect(() => {
    if (content?.data) {
      const artistsData = content.data as ArtistsContent
      reset(artistsData)
      setHasUnsavedChanges(false)
    }
  }, [content, reset])

  // Watch for changes
  useEffect(() => {
    if (content?.data) {
      const currentData = JSON.stringify(watchedData)
      const originalData = JSON.stringify(content.data)
      setHasUnsavedChanges(currentData !== originalData)
    }
  }, [watchedData, content])

  const onSubmit = async (data: ArtistsContent) => {
    const success = await saveContent(data)
    if (success) {
      setHasUnsavedChanges(false)
      toast.success('Artists section saved successfully!')
    } else {
      toast.error('Failed to save artists section')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading artists section...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-neutral-900">Artists Section</h2>
          <p className="text-sm text-neutral-600">Edit the call-to-action section at the bottom of the Artists page</p>
        </div>
        
        {hasUnsavedChanges && (
          <div className="flex items-center space-x-2 text-amber-600 text-sm">
            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
            <span>Unsaved changes</span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* CTA Section */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiUsers size={18} />
              <span>Call-to-Action Section</span>
            </h3>
          </div>
          
          <div className="space-y-6">
            {/* CTA Title */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                CTA Title
              </label>
              <input
                {...register('ctaTitle', { 
                  required: 'Title is required',
                  minLength: { value: 3, message: 'Title must be at least 3 characters' },
                  maxLength: { value: 100, message: 'Title must be less than 100 characters' }
                })}
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ready to Book Live Music?"
              />
              {errors.ctaTitle && (
                <p className="mt-1 text-sm text-red-600">{errors.ctaTitle.message}</p>
              )}
            </div>

            {/* CTA Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                CTA Description
              </label>
              <textarea
                {...register('ctaDescription', { 
                  required: 'Description is required',
                  minLength: { value: 10, message: 'Description must be at least 10 characters' },
                  maxLength: { value: 500, message: 'Description must be less than 500 characters' }
                })}
                rows={4}
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                placeholder="Contact us to discuss your event needs and we'll help you find the perfect musicians to make your occasion truly special."
              />
              {errors.ctaDescription && (
                <p className="mt-1 text-sm text-red-600">{errors.ctaDescription.message}</p>
              )}
            </div>

            {/* CTA Button Text */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Button Text
              </label>
              <input
                {...register('ctaButtonText', { 
                  required: 'Button text is required',
                  minLength: { value: 2, message: 'Button text must be at least 2 characters' },
                  maxLength: { value: 30, message: 'Button text must be less than 30 characters' }
                })}
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Get in Touch"
              />
              {errors.ctaButtonText && (
                <p className="mt-1 text-sm text-red-600">{errors.ctaButtonText.message}</p>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6 p-6 bg-gradient-to-r from-primary to-secondary rounded-xl text-white">
            <h4 className="text-sm font-medium text-white/80 mb-3">Preview</h4>
            <h3 className="font-bold mb-3 text-xl">{watchedData.ctaTitle}</h3>
            <p className="mb-4 text-sm opacity-90">{watchedData.ctaDescription}</p>
            <button className="bg-white text-primary font-semibold py-2 px-6 rounded-xl">
              {watchedData.ctaButtonText}
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={saving || !hasUnsavedChanges}
            className="flex items-center space-x-2 px-6 py-3 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="loading-spinner w-4 h-4"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ArtistsEditor

